import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";

/**
 * Loading silhouette for `PublicationCard` — mirrors its structure 1:1 so the
 * feed doesn't reflow when real cards fade in: journal line (size-6 tile + name)
 * → `gap-2` block of hero image (h-42, rounded-xl) + text (title heading-xs
 * 17/22, optional description footnote 13/20, author caption 11/16, meta/action
 * row). `showDescription` mirrors callers that pass a `description` (the feed)
 * vs those that don't (playlist rows pass `description={null}`). `showImage`
 * does the same for the hero: a list of cards with `imageUrl={null}` (the visual
 * abstract article picker) would otherwise jump by the image's 168px on load.
 */
function SkeletonPublicationCard({
  showDescription,
  showImage,
}: {
  showDescription: boolean;
  showImage: boolean;
}) {
  return (
    <div className="flex w-full flex-col items-start gap-1 font-dm-sans">
      {/* Journal line: square tile (size-6, rounded-lg) + "In {journal}". */}
      <div className="flex h-6 w-full items-center gap-1">
        <Skeleton width={24} height={24} className="shrink-0 rounded-lg" />
        <Skeleton $variant="rectangular" width="45%" height={13} />
      </div>

      <div className="flex w-full flex-col gap-2">
        {/* Hero image (h-42 = 168px, rounded-xl = 12px). */}
        {showImage ? (
          <Skeleton width="100%" height={168} className="rounded-xl" />
        ) : null}

        <div className="flex w-full flex-col gap-0.5">
          {/* Title (heading-xs, 2 lines) + description (footnote, 3 lines). */}
          <div className="flex w-full flex-col gap-1">
            <div className="flex w-full flex-col">
              <SkeletonTextLine width="100%" fontSize={17} lineHeight={22} />
              <SkeletonTextLine width="70%" fontSize={17} lineHeight={22} />
            </div>
            {showDescription ? (
              <div className="flex w-full flex-col">
                <SkeletonTextLine width="100%" fontSize={13} lineHeight={20} />
                <SkeletonTextLine width="100%" fontSize={13} lineHeight={20} />
                <SkeletonTextLine width="55%" fontSize={13} lineHeight={20} />
              </div>
            ) : null}
          </div>

          {/* Author (caption) + meta tag / action-icon row. The row is
              min-h-10 because the two 40x40 icon-button hit areas set its
              height; each holds a 24px glyph. */}
          <div className="flex w-full flex-col gap-1">
            <SkeletonTextLine width="35%" fontSize={11} lineHeight={16} />
            <div className="flex min-h-10 w-full items-center justify-between gap-2">
              <Skeleton width={72} height={24} className="rounded" />
              <div className="flex h-10 items-center gap-2">
                <div className="inline-flex size-10 items-center justify-center">
                  <Skeleton $variant="circular" width={24} height={24} />
                </div>
                <div className="inline-flex size-10 items-center justify-center">
                  <Skeleton $variant="circular" width={24} height={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SkeletonPublicationCard;
